import { useCallback, useRef, useState } from 'react';

/**
 * `showImportDialog(config)` as a promise, the way `importService` exposed it.
 *
 * The contract is what `DirectoryMigrationsManagerPage` was written against and is preserved
 * exactly: call it, a dialog opens, and the promise resolves with `{ finished, status }` when
 * the dialog closes. The caller does not render the dialog or hold its state.
 *
 * The resolver is held in a ref rather than state because resolving is not a render: storing
 * a function in `useState` would also mean React calling it as a lazy initialiser.
 */
export default function useImportDialog() {
  const [config, setConfig] = useState(null);
  const resolver = useRef(null);
  const openId = useRef(0);

  const showImportDialog = useCallback(
    (options = {}) =>
      new Promise((resolve) => {
        resolver.current = resolve;
        openId.current += 1;
        setConfig({
          openId: openId.current,
          title: options.title || 'Import Records',
          instruction: options.instruction,
          importType: options.importType,
          importParams: options.importParams,
        });
      }),
    []
  );

  const onClose = useCallback((result) => {
    setConfig(null);
    resolver.current?.(result);
    resolver.current = null;
  }, []);

  // `openId` increments on every open so the caller can use it as a React `key`. A
  // permanently-mounted dialog keeps the previous import's file, status and error flag in its
  // `useState`, so the next open would paint the last result, with "Download errors" already
  // showing. Angular's `$modal.open` built a fresh scope each time; keying on `openId` is the
  // same guarantee, and it is exactly the trap the link picker fell into in SURF-4809.
  const importDialogProps = config ? { ...config, onClose } : null;

  return { showImportDialog, importDialogProps };
}
